// src/features/auth/ForceChangePasswordPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Подключаем наш настроенный API клиент
import API from '../../api/axiosConfig';

function ForceChangePasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [tokenError, setTokenError] = useState(''); // Отдельная критическая ошибка токена
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Проверяем наличие временного токена при загрузке страницы
  useEffect(() => {
    const token = localStorage.getItem('reset_token');
    if (!token) {
      setTokenError('Отсутствует ключ доступа. Пожалуйста, авторизуйтесь заново через страницу входа.');
    }
  }, []);

  // Хэндлеры изменения полей, которые автоматически сбрасывают ошибку валидации
  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    if (error) setError(''); // Как только пользователь начинает вводить новый текст — оживляем форму
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (error) setError('');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    if (newPassword !== confirmPassword) {
      setError('Новый пароль и подтверждение пароля не совпадают.');
      setIsLoading(false);
      return;
    }

    const resetToken = localStorage.getItem('reset_token');
    if (!resetToken) {
      setTokenError('Сессия истекла или недействительна. Вернитесь на страницу входа.');
      setIsLoading(false);
      return;
    }

    try {
      // Отправляем запрос через централизованный API клиент (без хардкода IP)
      const res = await API.post('/api/force-change-password/', {
        reset_token: resetToken,
        new_password: newPassword,
        confirm_password: confirmPassword
      });

      setSuccess('Пароль успешно обновлен! Вход в систему...');

      // БЕСШОВНЫЙ СЦЕНАРИЙ: Записываем полученные боевые токены автоматического входа
      localStorage.setItem('access_token', res.data.access);
      localStorage.setItem('refresh_token', res.data.refresh);
      localStorage.setItem('user_data', JSON.stringify(res.data.user_data));
      
      // Удаляем временный токен сброса, он больше не нужен
      localStorage.removeItem('reset_token');

      // Через 1.5 секунды перекидываем пользователя на главную страницу
      setTimeout(() => {
        navigate('/');
        window.location.reload();
      }, 1500);

    } catch (err) {
      console.error("Техническая ошибка смены пароля:", err);
      if (err.response && err.response.data) {
        const resData = err.response.data;
        if (resData.error) {
          setError(resData.error);
        } else if (resData.detail) {
          setError(resData.detail);
        } else {
          setError('Ошибка валидации данных. Убедитесь, что пароль достаточно сложный.');
        }
      } else {
        setError('Не удалось связаться с сервером. Проверьте сеть.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="p-8 md:p-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 text-amber-600 rounded-2xl mb-4">
              <span className="text-3xl">🛡️</span>
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900">Безопасность аккаунта</h2>
            <p className="text-gray-500 mt-2">
              Установите новый надежный пароль для вашей учетной записи.
            </p>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-5">
            {/* Вывод критической ошибки токена */}
            {tokenError && (
              <div className="bg-red-100 border-l-4 border-red-600 p-4 rounded-xl">
                <p className="text-sm text-red-900 font-semibold">{tokenError}</p>
              </div>
            )}

            {/* Вывод обычных ошибок валидации */}
            {error && !tokenError && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-xl">
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-xl">
                <p className="text-sm text-green-700 font-medium">{success}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Новый пароль</label>
              <input
                type="password"
                required
                disabled={!!tokenError} // Блокируем только если вообще нет ключа доступа
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 outline-none transition-all font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Минимум 8 символов, включая цифры"
                value={newPassword}
                onChange={handleNewPasswordChange}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Подтвердите новый пароль</label>
              <input
                type="password"
                required
                disabled={!!tokenError}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-500 focus:ring-4 focus:ring-amber-500/10 outline-none transition-all font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Повторите новый пароль"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || !!tokenError} // Теперь кнопка набора текста доступна ВСЕГДА, кроме момента загрузки или отсутствия токена
              className={`w-full py-4 rounded-xl font-bold text-white shadow-lg transform transition-all active:scale-95 ${
                isLoading || !!tokenError ? 'bg-gray-400 cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-600'
              }`}
            >
              {isLoading ? 'Сохранение...' : 'Обновить пароль и войти'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ForceChangePasswordPage;